import React, { useState } from "react";
import { GetExportOrderData, getReferralCodes } from "../../actions/OrderAction";
import { exportToExcel } from "../../utils/ExcelUtils";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSession } from "../layout/SessionContext";
import { formatDate } from "../../utils/DateFormat";

const columns = [
  { key: "uid", label: "ID" },
  { key: "createdAt", label: "Date & time" },
  { key: "customer.mobileNumber", label: "Customer number" },
  { key: "destinationNumber", label: "Destination number" },
  { key: "paymentMethod", label: "Method" },
  { key: "product.brandName", label: "Brand" },
  { key: "product.productName", label: "Product" },
  { key: "customer.referral.code", label: "Referral code" },
  { key: "paymentStatus", label: "Payment status" },
  { key: "orderStatus", label: "Order status" },
  { key: "total.totalAmount", label: "Total(€)" }
];

const Header = ({ filters, searchTerm }) => {
  const [loading, setLoading] = useState(false);
  const { hasPermission } = useSession();
  const headerMap = {};
  columns.forEach((col) => {
    headerMap[col.key.replace(/\./g, "_")] = col.label;
  });

  // Helper function to process referral data (aligned with InternationalHeader.jsx)
  const processReferralData = (referralValue) => {
    console.log("Processing referral value in Header:", referralValue);
    if (referralValue === "NA") {
      return {
        referral: "",
        hasReferral: "false", // Backend expects string "false" for customer.referral: null
      };
    }
    if (referralValue && referralValue !== "") {
      return {
        referral: referralValue,
        hasReferral: "true", // Backend expects string "true" for specific referral ID
      };
    }
    return {
      referral: "", // No referral filter; fetch all orders
    };
  };

  const getOrders = async () => {
    try {
      setLoading(true);
      if (Object.keys(filters).length === 0 && !searchTerm) {
        toast.info("Please add filters or search term");
        return;
      }

      console.log("Filters received in Header:", JSON.stringify(filters, null, 2));

      // Process referral filter
      const referralData = processReferralData(filters?.referral);

      // Validate referral code if hasReferral is "true"
      if (referralData.hasReferral === "true") {
        const referralResponse = await getReferralCodes();
        if (!referralResponse.success) {
          toast.error("Failed to fetch referral codes");
          return;
        }
        const referralCodes = referralResponse.referralCodes.map((ref) => ({
          _id: ref.id,
          code: ref.code.toString(),
        }));
        const selectedReferral = referralCodes.find(
          (ref) => ref._id === referralData.referral
        );
        if (!selectedReferral) {
          toast.error("Invalid referral ID selected");
          return;
        }
        referralData.referral = selectedReferral._id; // Use _id if backend expects ObjectId
        // referralData.referral = selectedReferral.code; // Uncomment if backend expects code
      }

      const requestData = {
        uid: filters?.uid || "",
        orderStatus: filters?.orderStatus || "",
        productId: filters?.productId || "",
        paymentStatus: filters?.paymentStatus || "",
        fromDate: filters?.fromDate || "",
        toDate: filters?.toDate || "",
        search: searchTerm || "",
        ...referralData, // Include referral and hasReferral
      };

      console.log("Export request data:", JSON.stringify(requestData, null, 2));

      const response = await GetExportOrderData(requestData);

      if (response?.success) {
        exportToExcel(
          response.data.map((order) => ({
            ...order,
            createdAt: formatDate(order.createdAt),
            destinationNumber: order.destinationNumber ? `+${order.destinationNumber}` : "N/A",
            customer: {
              ...order.customer,
              referral: {
                code: order.customer?.referral?.code || "N/A"
              },
              mobileNumber: order.customer?.mobileNumber ? `+${order.customer.mobileNumber}` : "N/A"
            }
          })),
          "Orders",
          "Orders",
          headerMap
        );
      } else {
        toast.error(response?.message || "Failed to fetch orders");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:px-10">
      <div className="flex justify-between items-center gap-6">
        <h1 className="md:text-3xl text-[24px] leading-9 font-bold text-text1">Orders</h1>
        {hasPermission("Orders", "export") && (
          <button
            className="secondary_button  leading-[20px] break-words"
            onClick={getOrders}
            disabled={loading}
          >
            {loading ? "Exporting..." : "Export Excel"}
          </button>
        )}
      </div>
      <hr className="border-gray-300 mt-6" />
    </div>
  );
};

export default Header;